import { useEffect, useState } from "react";

import { useSearchDrivers } from "@web-app/api";
import { Icon, Input, LoadingIcon } from "@web-app/components";
import { AppFilterType } from "@web-app/types";

import TabPanelTitle from "./TabPanelTitle";
import { TabPanelWrapper } from "./TabPanelWrapper";

export default function AssigneesFilterTab({
  assigneeSearch,
  onChange,
  setAssigneeSearch,
  temporaryAppFilter,
}: {
  assigneeSearch: string;
  onChange: (value: Partial<AppFilterType>) => void;
  setAssigneeSearch: (value: string) => void;
  temporaryAppFilter: AppFilterType;
}) {
  const [debouncedSearch, setDebouncedSearch] = useState(assigneeSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(assigneeSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [assigneeSearch]);

  const {
    data: assignees,
    isSuccess,
    isLoading,
  } = useSearchDrivers(debouncedSearch);

  const onChangeAssignee = (value: string, assigneeId: string) => {
    onChange({
      assigneeIds:
        value === "true"
          ? [...temporaryAppFilter.assigneeIds, assigneeId]
          : temporaryAppFilter.assigneeIds.filter((a) => a !== assigneeId),
    });
  };

  return (
    <TabPanelWrapper>
      <>
        <TabPanelTitle>
          <div className="flex justify-between items-center gap-4 -mt-2">
            <p className="text-xs md:text-sm">Assignees</p>
            <Input
              type="search"
              value={assigneeSearch}
              placeholder="Search"
              className="bg-gray-100 border-none shadow-none focus:ring-0 focus:ring-offset-0"
              onChange={(value) => setAssigneeSearch(value)}
            />
          </div>
        </TabPanelTitle>
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div className="mb-4">
              <LoadingIcon className="h-4 lg:h-6 w-4 lg:w-6 text-gray-400 animate-spin" />
            </div>
            <p className="text-xs text-gray-500">Fetching assignees...</p>
          </div>
        )}
        {isSuccess && assignees && assignees?.length > 0 && (
          <>
            <div className="mb-2 mt-2 flex flex-col gap-1 overflow-y-auto lg:max-w-[260px] overflow-x-hidden">
              {assignees?.map((assignee) => (
                <Input
                  key={assignee?.id}
                  type="checkbox"
                  rightLabel={assignee?.name || ""}
                  value={
                    temporaryAppFilter.assigneeIds.includes(assignee?.id || "")
                      ? "true"
                      : "false"
                  }
                  onChange={(value) =>
                    onChangeAssignee(value, assignee?.id || "")
                  }
                />
              ))}
            </div>
            <p className="text-[10px] lg:text-xs text-gray-500 pb-4 items-center">
              <Icon name="circleExclamation" className="mr-1" />
              Can't find an assignee? Try searching with their name or phone
              number.
            </p>
          </>
        )}
        {isSuccess && !assignees && (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div className="mb-4">
              <Icon
                name="users"
                className="h-4 lg:h-8 w-4 lg:w-8 text-gray-300"
              />
            </div>
            <h3 className="text-xs lg:text-sm font-medium text-gray-900 mb-1">
              No assignees found
            </h3>
            <p className="text-[10px] lg:text-xs text-gray-500">
              Try searching with a different name or phone number
            </p>
          </div>
        )}
      </>
    </TabPanelWrapper>
  );
}
